/* eslint-disable @typescript-eslint/no-floating-promises */
import { Box } from '@tanium/react-box';
import { FormRow } from '@tanium/react-form';
import { FormTextInput } from '@tanium/react-form-text-input';
import { SectionContainer, SectionContainerSection } from '@tanium/react-section-container';
import { Stack, StackChild } from '@tanium/react-stack';
import { Field, FieldProps, FormikProps } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticatedApiContext } from '../../api/useApiClient';
import { useAsync } from '../../hooks/useAsync';
import { useAsyncEffect } from '../../hooks/useAsyncEffect';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';
import { ConfigureIdpRequest } from '../../models/IdpModels';
import { useOnInputChange } from '../InputHandlerUtils';
import { RenderEmailAttribute } from './IdentityProviderAttributeInformation';
import { RenderGroupAttribute } from './IdentityProviderGroupInformation';
import { IdentityProviderMetadataForm } from './IdentityProviderMetadataForm';
import { IdpDocumentationLinks } from './IdpDocumentationLinks';
import { IdpSetupTest } from './IdpSetupTest';
import { LoginDomainForm } from './LoginDomainForm';
import { ServiceProviderSettingsInformation } from './ServiceProviderSettingsInformation';

interface IdpSetupConfigurationProps {
  formData: FormikProps<ConfigureIdpRequest>;
  onClose: () => void;
}

export const IdpSetupConfiguration: React.VFC<IdpSetupConfigurationProps> = ({ formData, onClose }) => {
  const { t } = useTranslation();
  const formValues = useMemo(() => formData.values, [formData.values]);
  const { idpConfigs, createIdp, updateIdp } = useIdentityProviderConfigs();

  const apiClient = useAuthenticatedApiContext();

  const [idpId, setIdpId] = useState<string | undefined>(formValues.id);
  const [setupType, setSetupType] = useState('auto');
  const isUpdate = Boolean(formValues.id !== undefined);

  const [selectedRoleIds, setSelectedRoleIds] = useState(new Set<string>([]));

  useEffect(() => {
    const newIdpId = createIdp.value?.id;
    if (newIdpId && newIdpId !== idpId) {
      formData.resetForm({ values: { ...formValues, id: newIdpId } });
      setIdpId(newIdpId);
    }

    if (formData.values.roleMappers) {
      setSelectedRoleIds(new Set(Object.keys(formData.values.roleMappers)));
    }
  }, [createIdp.value, formData, formValues, idpId]);

  const applyIdpConfigChanges = useAsync(
    useCallback(async () => {
      const request: ConfigureIdpRequest = {
        id: formValues.id,
        name: formValues.name,
        displayName: formValues.displayName,
        domainNames: formValues.domainNames,
        emailAttributeName: formValues.emailAttributeName,
        roleMappers: formValues.roleMappers,
        samlMetadataUrl: formValues.samlMetadataUrl,
        samlMetadataFile: formValues.samlMetadataFile,
        enableTaniumConsoleAuth: formValues.enableTaniumConsoleAuth,
        enableAccountPortalAuth: formValues.enableAccountPortalAuth,
        enableIdpSignout: formValues.enableIdpSignout,
      };
      if (isUpdate) {
        await updateIdp.execute(request);
      } else {
        await createIdp.execute(request);
      }
    }, [formValues, isUpdate, updateIdp, createIdp])
  );

  const samlConfig = useAsync(useCallback((idpName: string) => apiClient.getSamlConfig(idpName), [apiClient]));
  useAsyncEffect(samlConfig, [formValues.name]);

  const onDisplayNameChange = useOnInputChange((displayName: string) =>
    formData.setFieldValue('displayName', displayName, true)
  );

  const steps: { title: string; step: JSX.Element; sideContent?: JSX.Element }[] = [
    {
      title: t('IDP Settings'),
      step: (
        <Box mt={2}>
          <ServiceProviderSettingsInformation
            onChangeSetupType={setSetupType}
            setupType={setupType}
            samlConfig={samlConfig}
            idpName={formValues.name}
          />
        </Box>
      ),
      sideContent: <IdpDocumentationLinks />,
    },
    {
      title: t('Identity Provider Attribute Setup'),
      step: <RenderEmailAttribute />,
    },
    {
      title: t('Role Assignment'),
      step: (
        <RenderGroupAttribute
          selectedRoleIds={selectedRoleIds}
          setSelectedRoleIds={setSelectedRoleIds}
          roleMapperData={formValues.roleMappers}
          setRoleMapperData={(data) => {
            formData.setFieldValue('roleMappers', data);
          }}
        />
      ),
    },
    {
      title: t('Identity Provider Metadata'),
      step: (
        <IdentityProviderMetadataForm
          metadata={{ url: formValues.samlMetadataUrl, xml: formValues.samlMetadataFile }}
          setMetadata={(samlMetadata) => {
            formData.setFieldValue('samlMetadataUrl', samlMetadata?.url ?? '');
            formData.setFieldValue('samlMetadataFile', samlMetadata?.xml ?? '');
          }}
        />
      ),
    },
    {
      title: t('Sign In Email Domain'),
      step: <LoginDomainForm formData={formData}></LoginDomainForm>,
    },
    // { TODO(AMP-433): enable this when IAS supports signing users out of their IDP
    //   title: t('Enable IDP Logout'),
    //   step: <EnableIdpSignoutForm />,
    // },
    {
      title: '',
      step: (
        <IdpSetupTest
          applicationResponse={applyIdpConfigChanges}
          formData={formData}
          idpId={idpId}
          onApplyChanges={applyIdpConfigChanges.execute}
          onClose={onClose}
        />
      ),
    },
  ];

  const stackedSteps = steps.map(({ title, step: elem, sideContent }, index) => (
    <Stack key={index} direction="row" itemSpacing={2}>
      <StackChild grow={1}>
        <SectionContainer sideContent={sideContent}>
          <SectionContainerSection title={title} size="lg">
            {elem}
          </SectionContainerSection>
        </SectionContainer>
      </StackChild>
    </Stack>
  ));

  const idpNameInputLabel = (
    <Stack itemSpacing={1}>
      <Box>{t('Identity Provider Name')}</Box>
    </Stack>
  );

  const domainNameErrMsg = useMemo(() => {
    // filter out the idp that is being edited
    const existingIdps = idpConfigs.filter((idp) => idp.id !== formData.values.id);
    const usedDisplayNames = existingIdps.map((idp) => idp.displayName);
    const usedIdpNames = existingIdps.map((x) => x.name);

    const formDisplayName = formData.values.displayName;
    const formName = formData.values.name ?? formData.values.displayName;
    if (usedDisplayNames.includes(formDisplayName) || usedIdpNames.includes(formName)) {
      return t('IDP name conflicts with an existing IDP configuration');
    }
    return formData.errors?.displayName;
  }, [formData.errors?.displayName, formData.values, idpConfigs, t]);

  const idpNameInput = (
    <Field name="displayName">
      {({ field }: FieldProps): React.ReactElement => (
        <FormRow dataKey="idp-name" label={idpNameInputLabel} errors={domainNameErrMsg} required>
          <FormTextInput {...field} onChange={onDisplayNameChange} />
        </FormRow>
      )}
    </Field>
  );

  return (
    <Box>
      <Box mb={4}>{idpNameInput}</Box>
      <Stack direction="column" itemSpacing={4}>
        {stackedSteps}
      </Stack>
    </Box>
  );
};
